import React, { useRef, useState } from 'react'
import { getConfirmLocale } from 'antd/es/modal/locale'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from 'src/icons/antd-compat'

import type { ButtonProps, ModalFuncProps } from '../antd-props'
import { Button } from '../Button.shadcn'

/* -------------------------------------------------------------------------
 * The dialog behind the imperative `Modal.confirm` / `Modal.info|success|
 * error|warning` API, in shadcn mode. `./imperative.tsx` mounts it; the
 * dispatcher in `./index.tsx` decides whether this or antd's own static runs.
 *
 * Radix AlertDialog rather than Dialog: antd's confirm has `role="alertdialog"`
 * and no dismissal path that is neither OK nor Cancel — clicking the mask does
 * nothing. AlertDialog is the primitive with exactly those semantics.
 *
 * Two things it does that the `update-design` reference does not:
 *
 *  • THE TITLE IS RENDERED ONCE. The reference put the title in a `sr-only`
 *    `AlertDialogTitle` AND again in a visible `aria-hidden` div, so every
 *    `getByText(title)` in the repo found two nodes — the retarget catalogue
 *    records `useQuickExport`'s "Found multiple elements with the text: Error
 *    occurred." as exactly that. Here `AlertDialogTitle` IS the visible title,
 *    and the `sr-only` fallback exists only for a title-less confirm (Radix
 *    requires an accessible name).
 *  • OK/CANCEL FALL BACK TO THE ACTIVE LOCALE, not to hard-coded English.
 *    `getConfirmLocale()` is antd's own module-level confirm locale, kept in
 *    step by the app's `ConfigProvider` (`src/App/AntdI18n.tsx`) — the same
 *    source antd's own confirm reads, so a DA/FI tenant is not handed an
 *    English button by flipping the toggle. This module cannot use React
 *    context for it: an imperative modal renders in its OWN React root,
 *    outside the provider tree.
 *
 * `onOk`/`onCancel` returning a Promise keep the button spinning and defer the
 * close until it settles; a rejection keeps the dialog open, as antd does.
 * ------------------------------------------------------------------------- */

export type ImperativeType =
  | 'confirm'
  | 'info'
  | 'success'
  | 'error'
  | 'warning'

export type ImperativeConfig = ModalFuncProps

const ICONS: Record<ImperativeType, { node: React.ReactNode; color: string }> =
  {
    confirm: { node: <ExclamationCircleFilled />, color: '#faad14' },
    info: { node: <InfoCircleFilled />, color: '#1677ff' },
    success: { node: <CheckCircleFilled />, color: '#52c41a' },
    error: { node: <CloseCircleFilled />, color: '#ff4d4f' },
    warning: { node: <ExclamationCircleFilled />, color: '#faad14' },
  }

// antd's own confirm width.
const DEFAULT_WIDTH = 416

const isThenable = (v: unknown): v is PromiseLike<unknown> =>
  !!v && typeof (v as PromiseLike<unknown>).then === 'function'

export function ImperativeConfirm({
  type,
  open,
  config,
  onClose,
}: {
  type: ImperativeType
  open: boolean
  config: ImperativeConfig
  onClose: () => void
}): JSX.Element {
  const [okLoading, setOkLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const locale = getConfirmLocale()

  // Same focus-restore gap as the declarative seam: Radix returns focus to its
  // own trigger, and an imperative confirm has none. The initialiser runs on
  // the first render, before Radix's mount-autofocus effect moves focus into
  // the panel, so this is the element the user was on when the confirm opened.
  const restoreFocusRef = useRef<HTMLElement | null>(
    document.activeElement as HTMLElement | null
  )

  const showCancel = config.okCancel ?? type === 'confirm'
  const icon = config.icon === undefined ? ICONS[type].node : config.icon
  const width = config.width ?? DEFAULT_WIDTH

  const runAndMaybeClose = async (
    fn: ModalFuncProps['onOk'] | ModalFuncProps['onCancel'],
    setLoading: (b: boolean) => void
  ) => {
    const result = (fn as (() => unknown) | undefined)?.()
    if (isThenable(result)) {
      setLoading(true)
      try {
        await result
      } catch {
        setLoading(false)
        // antd keeps the dialog open when the handler rejects.
        return
      }
      setLoading(false)
    }
    onClose()
  }

  const okButtonProps = (config.okButtonProps ?? {}) as ButtonProps
  const cancelButtonProps = (config.cancelButtonProps ?? {}) as ButtonProps

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        data-slot="modal-confirm"
        style={{ width, maxWidth: 'calc(100% - 2rem)' }}
        // Escape maps to antd's `keyboard` default: it cancels.
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          restoreFocusRef.current?.focus()
        }}
        onEscapeKeyDown={(event) => {
          if (config.keyboard === false) {
            event.preventDefault()
            return
          }
          runAndMaybeClose(config.onCancel, setCancelLoading)
        }}
      >
        <div className="flex gap-3">
          {icon != null && (
            <span
              data-slot="modal-confirm-icon"
              className="mt-0.5 shrink-0 text-[22px] leading-none"
              style={{ color: ICONS[type].color }}
            >
              {icon}
            </span>
          )}
          <div className="min-w-0 flex-1">
            {config.title != null ? (
              <AlertDialogTitle data-slot="modal-confirm-title">
                {config.title}
              </AlertDialogTitle>
            ) : (
              // Radix requires an accessible name even when antd's confirm
              // renders no visible heading.
              <AlertDialogTitle className="sr-only">
                {locale.justOkText ?? 'OK'}
              </AlertDialogTitle>
            )}
            {config.content != null && (
              <AlertDialogDescription
                asChild
                data-slot="modal-confirm-content"
                className="mt-2"
              >
                <div>{config.content}</div>
              </AlertDialogDescription>
            )}
            <div
              data-slot="modal-confirm-footer"
              className="mt-6 flex justify-end gap-2"
            >
              {showCancel && (
                <Button
                  htmlType="button"
                  loading={cancelLoading}
                  {...cancelButtonProps}
                  onClick={() =>
                    runAndMaybeClose(config.onCancel, setCancelLoading)
                  }
                >
                  {config.cancelText ?? locale.cancelText}
                </Button>
              )}
              <Button
                htmlType="button"
                type="primary"
                danger={config.okType === 'danger'}
                loading={okLoading}
                {...okButtonProps}
                onClick={() => runAndMaybeClose(config.onOk, setOkLoading)}
              >
                {config.okText ??
                  (showCancel ? locale.okText : locale.justOkText)}
              </Button>
            </div>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
